import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useState, type ReactNode } from 'react';
import { weekdayOf } from '@salon/scheduling';
import { api, type Salon } from '../lib/api';
import { useCalendar } from '../lib/calendar';
import { useLocale, useT, type TKey } from '../lib/i18n';
import { useCurrentLocation } from '../lib/location';
import { addDays, dateOf, formatDateShort, formatTime, SALON_TZ, startOfWeek, todayIn } from '../lib/time';
import { formatEuro, useFormatMinutes } from './forms';
import type { WeekHours } from './WeekHoursEditor';

// What sits beside a settings form on wide screens: how clients see what you set there.

const card = 'rounded-[var(--radius-card)] border border-line bg-surface p-5 shadow-[0_1px_2px_rgb(29_25_21/0.04)]';
const days = ['1', '2', '3', '4', '5', '6', '7'] as const;
const euro = (cents: number) => formatEuro(cents).replace(/,00$/, '');

/** A phone with the booking page in it, the same object as in the setup wizard. */
export function PhoneFrame({ label, children, footer }: { label: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <section aria-label={label} className="flex flex-col gap-3">
      <p className="eyebrow">{label}</p>
      <div className="mx-auto w-full max-w-[340px] rounded-[44px] bg-frame p-2.5 shadow-[0_24px_60px_rgb(29_25_21/0.22)]">
        <div className="relative flex h-[600px] flex-col gap-4 overflow-hidden rounded-[36px] bg-canvas px-5 pb-5 pt-9">
          <span aria-hidden className="absolute left-1/2 top-2.5 h-5 w-24 -translate-x-1/2 rounded-full bg-frame" />
          {children}
          {footer}
        </div>
      </div>
    </section>
  );
}

function useSalon(orgId: string) {
  return useQuery({ queryKey: ['salon', orgId], queryFn: () => api<Salon>(`/orgs/${orgId}`) });
}

type CatalogService = { id: string; name: string; priceType: 'fixed' | 'from'; priceCents: number; clientMinutes: number; onlineBookable: boolean };
function useCatalog(orgId: string) {
  return useQuery({ queryKey: ['catalog', orgId], queryFn: () => api<{ categories: Array<{ id: string; name: string; services: CatalogService[] }> }>(`/orgs/${orgId}/catalog`) });
}

function BookingHeader({ name }: { name: string }) {
  const t = useT();
  return (
    <>
      <div className="relative">
        <p className="eyebrow !text-[9.5px]">{t('welcome.preview.label')}</p>
        <p className="display mt-1 truncate text-[30px]">{name}</p>
      </div>
      <div className="relative grid grid-cols-3 gap-1.5" aria-hidden>
        <span className="h-[3px] rounded-full bg-ink" />
        <span className="h-[3px] rounded-full bg-line" />
        <span className="h-[3px] rounded-full bg-line" />
      </div>
    </>
  );
}

/** The service menu as clients pick from it: online bookable services per category. */
function ServiceMenu({ orgId, limit }: { orgId: string; limit?: number }) {
  const t = useT();
  const fmt = useFormatMinutes();
  const catalog = useCatalog(orgId);
  let left = limit ?? Infinity;
  const groups = (catalog.data?.categories ?? [])
    .map((c) => ({ ...c, services: c.services.filter((s) => s.onlineBookable) }))
    .filter((c) => c.services.length > 0)
    .map((c) => {
      const services = c.services.slice(0, Math.max(0, left));
      left -= services.length;
      return { ...c, services };
    })
    .filter((c) => c.services.length > 0);
  return (
    <div className="relative flex min-h-0 flex-col gap-3 overflow-hidden">
      {groups.map((c) => (
        <div key={c.id} className="flex flex-col gap-1.5">
          <p className="eyebrow !text-[9.5px]">{c.name}</p>
          <ul className="flex flex-col overflow-hidden rounded-[14px] border border-line bg-surface">
            {c.services.map((s) => (
              <li key={s.id} className="flex items-center gap-2 border-b border-line px-3 py-2.5 last:border-b-0">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold">{s.name}</span>
                  <span className="block text-[11px] text-muted">{fmt(s.clientMinutes)}</span>
                </span>
                <span className="text-[12px] font-semibold tabular-nums">
                  {s.priceType === 'from' && <span className="font-normal text-muted">{t('aside.from')} </span>}
                  {euro(s.priceCents)}
                </span>
                <span aria-hidden className="flex size-5 items-center justify-center rounded-full border border-line text-[11px]">
                  +
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function StickyBar() {
  const t = useT();
  return (
    <div className="absolute inset-x-4 bottom-4 flex items-center justify-between rounded-full bg-frame py-1.5 pl-4 pr-1.5 text-canvas">
      <span className="text-[11px] text-canvas/70">{t('welcome.preview.bar')}</span>
      <span className="rounded-full bg-accent px-3 py-1.5 text-[11px] font-medium text-on-accent">{t('welcome.preview.next')}</span>
    </div>
  );
}

export function ServicesAside({ orgId }: { orgId: string }) {
  const t = useT();
  const salon = useSalon(orgId);
  const catalog = useCatalog(orgId);
  const offline = (catalog.data?.categories ?? []).flatMap((c) => c.services).filter((s) => !s.onlineBookable).length;
  return (
    <>
      <PhoneFrame label={t('aside.services')} footer={<StickyBar />}>
        <BookingHeader name={salon.data?.salon.name ?? ''} />
        <ServiceMenu orgId={orgId} />
      </PhoneFrame>
      {offline > 0 && <p className="text-center text-[13px] text-muted">{offline === 1 ? t('aside.offlineOne') : t('aside.offline', { n: offline })}</p>}
    </>
  );
}

/** The booking page as a whole: the menu, and who clients can choose, or the "closed" state. */
export function BookingAside({ orgId, enabled, staffChoice }: { orgId: string; enabled: boolean; staffChoice: boolean }) {
  const t = useT();
  const salon = useSalon(orgId);
  const team = useQuery({ queryKey: ['team', orgId], queryFn: () => api<{ team: Array<{ id: string; displayName: string; color: string; bookableOnline: boolean }> }>(`/orgs/${orgId}/team`) });
  const people = (team.data?.team ?? []).filter((m) => m.bookableOnline);
  return (
    <PhoneFrame label={t('aside.booking')} footer={enabled ? <StickyBar /> : undefined}>
      <BookingHeader name={salon.data?.salon.name ?? ''} />
      {enabled ? (
        <>
          {staffChoice && people.length > 1 && (
            <div className="relative flex flex-col gap-1.5">
              <p className="eyebrow !text-[9.5px]">{t('aside.who')}</p>
              <div className="flex flex-wrap gap-1.5">
                <span className="rounded-full bg-ink px-2.5 py-1 text-[11px] font-medium text-canvas">{t('aside.anyone')}</span>
                {people.slice(0, 4).map((m) => (
                  <span key={m.id} className="flex items-center gap-1 rounded-full border border-line bg-surface px-2 py-1 text-[11px]">
                    <span className="size-2 rounded-full" style={{ backgroundColor: m.color }} />
                    {m.displayName}
                  </span>
                ))}
              </div>
            </div>
          )}
          <ServiceMenu orgId={orgId} limit={staffChoice && people.length > 1 ? 4 : 5} />
        </>
      ) : (
        <p className="relative rounded-[14px] border border-dashed border-line bg-surface px-4 py-6 text-center text-[13px] text-muted">{t('aside.bookingOff')}</p>
      )}
    </PhoneFrame>
  );
}

type Holiday = { date: string; name: { nl: string; en: string }; closed: boolean };
type Closure = { id: string; dateFrom: string; dateUntil: string; message: string | null };

/** Opening hours the way the booking page lists them, and what's closed soon. */
export function HoursAside({ orgId, hours }: { orgId: string; hours: WeekHours | null }) {
  const t = useT();
  const locale = useLocale();
  const location = useCurrentLocation(orgId);
  const base = `/orgs/${orgId}/locations/${location.data?.id}`;
  const holidays = useQuery({ queryKey: ['holidays', location.data?.id], queryFn: () => api<{ holidays: Holiday[] }>(`${base}/holidays`), enabled: !!location.data });
  const closures = useQuery({ queryKey: ['closures', location.data?.id], queryFn: () => api<{ closures: Closure[] }>(`${base}/closures`), enabled: !!location.data });
  const today = todayIn(SALON_TZ);
  const todayKey = String(weekdayOf(today));
  const coming = [
    ...(holidays.data?.holidays ?? []).filter((h) => h.closed && h.date >= today).map((h) => ({ key: h.date, from: h.date, until: h.date, name: h.name[locale] })),
    ...(closures.data?.closures ?? []).filter((c) => c.dateUntil >= today).map((c) => ({ key: c.id, from: c.dateFrom, until: c.dateUntil, name: c.message ?? t('hoursPage.closures') })),
  ]
    .sort((a, b) => a.from.localeCompare(b.from))
    .slice(0, 6);
  return (
    <>
      <section className={card}>
        <h2 className="eyebrow">{t('aside.hours')}</h2>
        <ul className="mt-3 flex flex-col">
          {days.map((d) => {
            const ranges = hours?.[d] ?? [];
            return (
              <li key={d} className={`flex items-baseline justify-between gap-3 rounded-[10px] px-2.5 py-1.5 text-[15px] ${d === todayKey ? 'bg-accent-soft' : ''}`}>
                <span className={d === todayKey ? 'font-semibold' : ''}>{t(`day.${d}` as TKey)}</span>
                <span className={`tabular-nums ${ranges.length ? '' : 'text-muted'}`}>{ranges.length ? ranges.map((r) => `${r.start} – ${r.end}`).join(', ') : t('hours.closed')}</span>
              </li>
            );
          })}
        </ul>
      </section>
      <section className={card}>
        <h2 className="eyebrow">{t('aside.closures')}</h2>
        {coming.length === 0 ? (
          <p className="mt-2 text-[14px] text-muted">{t('aside.noClosures')}</p>
        ) : (
          <ul className="mt-2 flex flex-col">
            {coming.map((c) => (
              <li key={c.key} className="flex items-baseline justify-between gap-3 border-t border-line py-2 text-[14px] first:border-t-0">
                <span className="min-w-0 truncate">{c.name}</span>
                <span className="shrink-0 tabular-nums text-muted first-letter:uppercase">
                  {formatDateShort(c.from, locale)}
                  {c.until !== c.from && ` – ${formatDateShort(c.until, locale)}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

type Business = { name: string; phone: string | null; email: string | null; address: { street: string | null; houseNumber: string | null; postcode: string | null; city: string | null } };

/** The salon's details where clients meet them: the booking page header and the foot of every email. */
export function BusinessAside({ form }: { form: Business }) {
  const t = useT();
  const a = form.address;
  const street = [a.street, a.houseNumber].filter(Boolean).join(' ');
  const line = [street, [a.postcode, a.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return (
    <>
      <p className="eyebrow">{t('aside.business')}</p>
      <section className={`${card} relative overflow-hidden`}>
        <p className="eyebrow relative !text-[10px]">{t('aside.onBooking')}</p>
        <p className="display relative mt-2 text-[34px] leading-tight">{form.name || '…'}</p>
        {line && <p className="relative mt-1 text-[14px] text-muted">{line}</p>}
        {form.phone && <p className="relative text-[14px] font-semibold text-accent-strong">{form.phone}</p>}
      </section>
      <section className={card}>
        <p className="eyebrow !text-[10px]">{t('aside.inEmails')}</p>
        <div className="mt-3 border-t border-dashed border-line pt-3 text-center text-[12px] leading-relaxed text-muted">
          <p className="font-semibold text-ink">{form.name}</p>
          {line && <p>{line}</p>}
          <p>{[form.phone, form.email].filter(Boolean).join(' · ')}</p>
        </div>
      </section>
    </>
  );
}

const clientKinds = ['confirmation', 'reminder', 'changed', 'cancelled'] as const;

/** The real email, as the client gets it, for the message you pick. */
export function MessageAside({ orgId, settings }: { orgId: string; settings: unknown }) {
  const t = useT();
  const locale = useLocale();
  const [kind, setKind] = useState<(typeof clientKinds)[number]>('confirmation');
  const preview = useQuery({
    queryKey: ['message-preview', orgId, kind, JSON.stringify(settings), locale],
    queryFn: () => api<{ subject: string; html: string }>(`/orgs/${orgId}/message-settings/preview`, { method: 'POST', body: JSON.stringify({ kind, settings, locale }) }),
    placeholderData: (p) => p,
  });
  return (
    <>
      <p className="eyebrow">{t('aside.email')}</p>
      <div role="tablist" className="flex flex-wrap gap-1 rounded-[18px] border border-line bg-surface p-1">
        {clientKinds.map((k) => (
          <button key={k} role="tab" aria-selected={kind === k} onClick={() => setKind(k)} className={`rounded-full px-3 py-1.5 text-[13px] font-medium ${kind === k ? 'bg-ink text-canvas' : 'text-muted hover:text-ink'}`}>
            {t(`msg.${k}` as TKey)}
          </button>
        ))}
      </div>
      <section className="overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface shadow-[0_1px_2px_rgb(29_25_21/0.04)]">
        {preview.data && <p className="truncate border-b border-line px-4 py-2.5 text-[13px] font-medium">{preview.data.subject}</p>}
        <iframe title={t('aside.email')} sandbox="allow-same-origin" srcDoc={preview.data?.html ?? ''} className="h-[620px] w-full bg-canvas" />
      </section>
    </>
  );
}

/** Who works when this week, straight from the calendar (own hours, holidays and time off included). */
export function TeamWeekAside({ orgId }: { orgId: string }) {
  const t = useT();
  const locale = useLocale();
  const location = useCurrentLocation(orgId);
  const today = todayIn(SALON_TZ);
  const monday = startOfWeek(today);
  const week = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const cal = useCalendar(orgId, location.data?.id, monday, week[6]!);
  const short = (iso: string) => formatTime(iso, SALON_TZ).replace(/^0/, '').replace(/:00$/, '');
  const head = (d: string) => new Intl.DateTimeFormat(locale === 'nl' ? 'nl-NL' : 'en-GB', { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`)).slice(0, 2);
  return (
    <section className={card}>
      <h2 className="eyebrow">{t('aside.teamWeek')}</h2>
      <p className="mt-1 text-[13px] text-muted">{t('aside.teamWeekHint')}</p>
      <div className="mt-4 grid grid-cols-[minmax(0,1fr)_repeat(7,36px)] items-center gap-y-2 text-center text-[11px]">
        <span />
        {week.map((d) => (
          <span key={d} className={`eyebrow !text-[10px] ${d === today ? 'text-accent-strong' : ''}`}>
            {head(d)}
          </span>
        ))}
        {(cal.data?.staff ?? []).map((s) => (
          <div key={s.id} className="contents">
            <span className="flex min-w-0 items-center gap-2 text-left text-[14px] font-medium">
              <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
              <span className="truncate">{s.displayName}</span>
            </span>
            {week.map((d) => {
              const ranges = s.scheduled.filter((r) => dateOf(r.start, SALON_TZ) === d);
              return (
                <span
                  key={d}
                  title={ranges.map((r) => `${formatTime(r.start, SALON_TZ)}–${formatTime(r.end, SALON_TZ)}`).join(', ')}
                  className={`mx-0.5 rounded-[8px] py-1.5 font-medium leading-tight tabular-nums ${ranges.length ? '' : 'text-muted/60'} ${d === today ? 'ring-1 ring-accent' : ''}`}
                  style={ranges.length ? { backgroundColor: `color-mix(in srgb, ${s.color} 16%, var(--color-surface))` } : undefined}
                >
                  {ranges.length ? (
                    <>
                      {short(ranges[0]!.start)}
                      <span className="block text-muted">{short(ranges.at(-1)!.end)}</span>
                    </>
                  ) : (
                    '—'
                  )}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
}

const HelpLinks = lazy(() => import('./HelpLinks'));

/** The help articles that belong to this page (loaded separately, like the help panel). */
export function HelpAside({ slugs }: { slugs: string[] }) {
  return (
    <Suspense fallback={null}>
      <HelpLinks slugs={slugs} />
    </Suspense>
  );
}
